import React, { useState } from 'react';
import { updatePrivacyPolicyStatus } from '../../services/privacyPolicyService';
import { userAppContext } from '../../Context/Context';
import SharedButton from '../../global/Button/Button';

const PrivacyPolicyModal = ({ isOpen, onClose, onAccept }) => {
  const [isAccepted, setIsAccepted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { user } = userAppContext();

  const handleAccept = async () => {
    if (!isAccepted) {
      return;
    }

    setIsLoading(true);
    try {
      const userId = user?.sub || user?.username;
      await updatePrivacyPolicyStatus(userId, true);
      onAccept();
    } catch (error) {
      console.error('Error accepting privacy policy:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDecline = () => {
    setIsAccepted(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Privacy Policy</h2>
        </div>

        <div className="p-6 overflow-y-auto max-h-96">
          <div className="prose prose-sm max-w-none">
            <p className="mb-4">
              To allow you to use the filter management functionality, we need you to accept our privacy
              policies.
            </p>

            <h3 className="text-lg font-medium mb-3">Personalization data (saved searches and filters)</h3>
            <p className="mb-4">
              If you choose to save searches or filters within your user profile, we will process the
              information necessary to store your preferences and allow you to retrieve and manage them later.
              These data are used exclusively to provide this personalization functionality within your
              password-protected account. They are not shared with third parties and are accessible only to
              you when authenticated.
            </p>

            <h3 className="text-lg font-medium mb-3">Legal Basis</h3>
            <p className="mb-4">
              The legal basis for processing these data is the performance of the contract (Article 6.1.b of
              the GDPR), as these features are part of the services offered to registered users. Saving
              searches or filters occurs only when you voluntarily request it from your profile; no automatic
              saving takes place. There is no functionality for sharing searches or filters with third
              parties, and this data remains accessible only within your authenticated account.
            </p>

            <h3 className="text-lg font-medium mb-3">Data Retention</h3>
            <p className="mb-4">
              Saved searches and filters are retained while your account remains active or until you delete
              them manually from your profile. If your account or saved filters remain inactive for more than
              24 months — meaning you have not accessed or used them during that period — these
              personalization data may be deleted after prior notice. Minimal technical records (such as
              identifiers and timestamps) may be kept for up to 12 additional months solely for security and
              auditing purposes.
            </p>

            <h3 className="text-lg font-medium mb-3">Security Measures</h3>
            <p className="mb-4">
              Saved searches and filters are stored in encrypted form and protected by access-control
              mechanisms that ensure only the authenticated user can access their own data.
            </p>

            <p className="text-sm text-gray-600 mt-6">
              For more information about our full privacy policies, please visit our{' '}
              <a
                href="/Privacy-policy"
                className="text-blue-600 hover:underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                privacy policy
              </a>
              .
            </p>
          </div>
        </div>

        <div className="p-6 border-t border-gray-200">
          <div className="flex items-center mb-4">
            <input
              type="checkbox"
              id="privacy-accept"
              checked={isAccepted}
              onChange={e => setIsAccepted(e.target.checked)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="privacy-accept" className="ml-2 text-sm text-gray-700 mb-0">
              I&apos;ve read and accept the privacy policies for the use of saved filters
            </label>
          </div>

          <div className="flex justify-end space-x-3">
            <SharedButton variant="secondary" onClick={handleDecline} disabled={isLoading}>
              Cancel
            </SharedButton>
            <SharedButton onClick={handleAccept} disabled={!isAccepted || isLoading} loading={isLoading}>
              {isLoading ? 'Loading...' : 'Accept and continue'}
            </SharedButton>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyModal;
